import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Colors } from 'works/legacy/lib/style-helper';
import { RateCurrencyInput } from './_rate-currency-input';
import { Tooltip } from 'antd';
import {
  ALL_INPUT_KEYS,
  CORE_FLOAT_KEYS_MODEL,
  WEEK_DAY_LABELS,
  WEEKDAY_KEYS_MODEL,
} from '@scheduling/services';

function RatesInputsRow({ title, keys, onClear, onChange, increment }) {
  return (
    <tr key={title}>
      <th>{title}</th>

      <MasterRateInputCell
        title={title}
        increment={increment}
        onClear={onClear}
        onChange={onChange}
        testid={title}
      />

      {keys.map((key) => (
        <td key={key} className="rates-input-cell">
          <RateCurrencyInput
            required
            increment={increment}
            validateIncrement
            name={key}
            data-testid={key}
          />
        </td>
      ))}
    </tr>
  );
}

function MasterRateInputCell({
  onMouseEnter,
  onMouseLeave,
  title = '',
  increment,
  onClear,
  onChange,
  testid,
}) {
  const tooltipTitle = `Fill all ${title} rates with the same value.`;
  const [value, setValue] = useState(null);
  function _onClear() {
    setValue(null);
    onClear();
  }

  function _onChange(value) {
    setValue(value);
    onChange(value);
  }

  return (
    <td
      className="master-input-cell"
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <Tooltip title={tooltipTitle} overlayStyle={{ maxWidth: 300 }}>
        <RateCurrencyInput
          data-testid={`${testid}-master-input`}
          value={value}
          increment={increment}
          onChange={_onChange}
          onClear={_onClear}
        />
      </Tooltip>
    </td>
  );
}

function ColumnLabel({ label }) {
  return (
    <span key={label} className="ant-form-item-label text-capitalize">
      <label className="ant-form-item-required">{label}</label>
    </span>
  );
}

export function VariableRatesInputsTable({
  setFields,
  cleanFields,
  increment,
}) {
  const [paintInputsBackground, setPaintInputBackground] = useState(false);

  function setBatchFields(value, keys) {
    const payload = {};
    keys.forEach((key) => (payload[key] = value));
    setFields(payload);
  }

  return (
    <>
      <table
        className={`variable-rates-inputs-table ${
          paintInputsBackground ? 'main-master-rate-input--hover' : ''
        }`}
      >
        <thead>
          <tr>
            <th></th>
            <th></th>
            {WEEK_DAY_LABELS.map((label) => (
              <th key={label}>
                <ColumnLabel label={label} />
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr>
            <th></th>
            <MasterRateInputCell
              testid="main"
              keys={ALL_INPUT_KEYS}
              increment={increment}
              onMouseEnter={() => setPaintInputBackground(true)}
              onMouseLeave={() => setPaintInputBackground(false)}
              onClear={() => cleanFields(ALL_INPUT_KEYS)}
              onChange={(value) => setBatchFields(value, ALL_INPUT_KEYS)}
            />
            {WEEKDAY_KEYS_MODEL.map(({ title, keys }) => (
              <MasterRateInputCell
                title={title}
                key={title}
                testid={title}
                increment={increment}
                onClear={() => cleanFields(keys)}
                onChange={(value) => setBatchFields(value, keys)}
              />
            ))}
          </tr>
          {CORE_FLOAT_KEYS_MODEL.map(({ title, keys }) => (
            <RatesInputsRow
              title={title}
              key={title}
              keys={keys}
              increment={increment}
              onClear={() => cleanFields(keys)}
              onChange={(value) => setBatchFields(value, keys)}
            />
          ))}
        </tbody>
      </table>
      <style jsx>{`
        .variable-rates-inputs-table {
          width: 100%;
          overflow: hidden;
        }
        .variable-rates-inputs-table
          :global(.master-input-cell .ant-input-number-group-wrapper) {
          opacity: 0.5;
        }
        .variable-rates-inputs-table
          :global(.master-input-cell .ant-input-number-group-wrapper):hover,
        .variable-rates-inputs-table
          :global(
            .master-input-cell
              .ant-input-number-group-wrapper.ant-input-number-focused
          ) {
          opacity: 1;
        }
        .variable-rates-inputs-table :global(th) {
          font-weight: inherit;
          background-color: ${Colors.white};
        }
        .variable-rates-inputs-table :global(tr th:first-child) {
          width: 110px;
        }
        .variable-rates-inputs-table :global(td),
        .variable-rates-inputs-table :global(th) {
          padding: 0 16px;
          position: relative;
        }
        .variable-rates-inputs-table :global(.ant-form-item) {
          margin: 16px 0;
        }

        .variable-rates-inputs-table :global(.ant-form-item-with-help) {
          margin-bottom: 0;
        }
        .variable-rates-inputs-table :global(.ant-form-item-explain) {
          min-height: 16px;
          line-height: 15px;
          padding-top: 1px;
        }

        .variable-rates-inputs-table :global(td.master-input-cell) {
          background-color: ${Colors.elmHyperlight};
        }

        .variable-rates-inputs-table :global(.master-input-cell:hover),
        .variable-rates-inputs-table
          :global(.master-input-cell:hover ~ td:not(.master-input-cell)),
        .variable-rates-inputs-table.main-master-rate-input--hover
          :global(.rates-input-cell) {
          background-color: ${Colors.elmLightest};
        }

        .variable-rates-inputs-table :global(.master-input-cell:hover::after) {
          background-color: ${Colors.elmLightest};
          content: '';
          height: 10000px;
          left: 0;
          position: absolute;
          top: 64px;
          width: 100%;
          z-index: 0;
        }
      `}</style>
    </>
  );
}

RatesInputsRow.propTypes = {
  keys: PropTypes.arrayOf(PropTypes.string).isRequired,
  title: PropTypes.string.isRequired,
  increment: PropTypes.number,
  onClear: PropTypes.func,
  onChange: PropTypes.func,
};

MasterRateInputCell.propTypes = {
  title: PropTypes.string,
  testid: PropTypes.string,
  increment: PropTypes.number,
  onClear: PropTypes.func,
  onChange: PropTypes.func,
  onMouseEnter: PropTypes.func,
  onMouseLeave: PropTypes.func,
};

ColumnLabel.propTypes = {
  label: PropTypes.string.isRequired,
};

VariableRatesInputsTable.propTypes = {
  cleanFields: PropTypes.func.isRequired,
  setFields: PropTypes.func.isRequired,
  increment: PropTypes.number,
};
